import React, { useState } from 'react';
import { formatCurrency } from "../utils/formatCurrency";
import Decimal from 'decimal.js';
import { MarketPrices, WalletHoldingsUsd } from '../../../models/bmp.model';
import { formatCrypto } from '../../../utils/formatCrypto';
import { FixRealizationModal } from '../modals/FixRealizationModal';

interface FixWalletProps {
    holdings: WalletHoldingsUsd | null;
    prices: MarketPrices | null;
    onBack: () => void;
    onUpdate: () => void; // Added callback to trigger parent asset state refreshes seamlessly
}

export const FixWalletComponent: React.FC<FixWalletProps> = ({ holdings, prices, onBack, onUpdate }) => {
    const [isRealizeModalOpen, setIsRealizeModalOpen] = useState<boolean>(false);

    // Értékek kezelése
    const fixBtcUsd = new Decimal(holdings?.fixBtc || 0);
    const fixEthUsd = new Decimal(holdings?.fixEth || 0);
    const totalValue = fixBtcUsd.plus(fixEthUsd);

    // Mennyiségek kalkulációja
    const btcAmount = prices?.btc && !fixBtcUsd.isZero() 
        ? fixBtcUsd.div(prices.btc).toNumber() 
        : 0;
    
    const ethAmount = prices?.eth && !fixEthUsd.isZero() 
        ? fixEthUsd.div(prices.eth).toNumber() 
        : 0;

    return (
        <div className="w-full max-w-6xl mx-auto p-6 animate-in fade-in duration-700">
            {/* Header Navigáció */}
            <div className="flex justify-between items-center mb-8">
                <button onClick={onBack} className="text-slate-500 hover:text-slate-800 font-bold flex items-center gap-2 transition-colors group">
                    <span className="group-hover:-translate-x-1 transition-transform">←</span> Vissza a portfólióhoz
                </button>

                {/* Added Action Button for realizing cold storage holdings */}
                <button
                    onClick={() => setIsRealizeModalOpen(true)}
                    disabled={totalValue.isZero()}
                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:hover:bg-amber-600 text-white px-6 py-3 rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-amber-600/10 transition-all active:scale-95"
                >
                    Fix pozíció realizálása
                </button>
            </div>

            {/* Fő információs kártya */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                <div className="lg:col-span-3 bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-2xl relative overflow-hidden flex flex-col justify-center min-h-[220px]">
                    <div className="relative z-10">
                        <h2 className="text-amber-500 text-[10px] font-black uppercase tracking-[0.3em] mb-3">Fix Portfólió Összérték</h2>
                        <div className="text-6xl font-black tracking-tighter">
                            {formatCurrency(totalValue.toNumber())}
                        </div>
                        <p className="mt-4 text-slate-400 text-xs font-medium max-w-md">
                            Hosszútávú biztonsági tartalék (Cold Storage), amely Bitcoin és Ethereum alapú fix eszközöket tartalmaz.
                        </p>
                    </div>
                    {/* Háttér dekoráció */}
                    <div className="absolute right-[-20px] bottom-[-40px] opacity-[0.03] text-[15rem] font-black italic select-none text-white">FIX</div>
                </div>
            </div>

            {/* Asset Holdings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {/* BTC Kártya */}
                <div className="bg-orange-50/50 border border-orange-100 rounded-3xl p-8 flex justify-between items-center group hover:bg-orange-50 transition-all hover:shadow-md">
                    <div>
                        <p className="text-orange-600 text-[10px] font-black uppercase tracking-widest mb-1">Bitcoin Fix Holding</p>
                        <div className="text-3xl font-black text-slate-900 mb-1">
                            {formatCurrency(fixBtcUsd.toNumber())}
                        </div>
                        <p className="text-sm font-bold text-slate-500">
                            {formatCrypto(btcAmount)} <span className="text-[10px] opacity-60">BTC</span>
                        </p>
                    </div>
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm text-2xl group-hover:scale-110 transition-transform">₿</div>
                </div>

                {/* ETH Kártya */}
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-3xl p-8 flex justify-between items-center group hover:bg-indigo-50 transition-all hover:shadow-md">
                    <div>
                        <p className="text-indigo-600 text-[10px] font-black uppercase tracking-widest mb-1">Ethereum Fix Holding</p>
                        <div className="text-3xl font-black text-slate-900 mb-1">
                            {formatCurrency(fixEthUsd.toNumber())}
                        </div>
                        <p className="text-sm font-bold text-slate-500">
                            {formatCrypto(ethAmount)} <span className="text-[10px] opacity-60">ETH</span>
                        </p>
                    </div>
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm text-2xl group-hover:scale-110 transition-transform">Ξ</div>
                </div>
            </div>

            {/* Embed the Realization Dialog view */}
            <FixRealizationModal
                isOpen={isRealizeModalOpen}
                onClose={() => setIsRealizeModalOpen(false)}
                maxBtc={btcAmount}
                maxEth={ethAmount}
                marketPrices={prices ? { btc: prices.btc.toString(), eth: prices.eth.toString() } : null}
                onSuccess={() => {
                    setIsRealizeModalOpen(false);
                    onUpdate(); // Re-fetch portfolio numbers automatically upon submission success
                }}
            />
        </div>
    );
};